/**
 * Auth Context – Global authentication state management
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  getStoredUser,
  setStoredUser,
  clearTokens,
  isAuthenticated as checkAuth,
  startTokenRefresh,
  stopTokenRefresh,
  getCurrentUser,
} from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load user from localStorage on mount
    const storedUser = getStoredUser();
    if (storedUser && checkAuth()) {
      setUser(storedUser);
      startTokenRefresh();
      
      // Verify token is still valid by fetching current user
      getCurrentUser()
        .then((fetchedUser) => {
          setUser(fetchedUser);
        })
        .catch(() => {
          // Token invalid, clear everything
          clearTokens();
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }

    return () => {
      stopTokenRefresh();
    };
  }, []);

  const login = (newUser: User) => {
    setUser(newUser);
    setStoredUser(newUser);
    startTokenRefresh();
  };

  const logout = () => {
    setUser(null);
    clearTokens();
    stopTokenRefresh();
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    setStoredUser(updatedUser);
  };

  const refreshUser = async () => {
    try {
      const fetchedUser = await getCurrentUser();
      setUser(fetchedUser);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        updateUser,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
